import { useHistory } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import '../../styles/page-components.css';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChatService from '../../app/services/hooks/useChatService';
import DefaultModal from '../../components/default/DefaultModal';
import ChatDocumentCreate from './components/ChatDocumentCreate';
import ChatDocumentEdit from './components/ChatDocumentEdit';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import ChatDocumentDataTable from './components/tables/ChatDocumentDataTable';
import { toast } from 'react-toastify';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';

interface ChatDocument {
    id: number;
    name: string;
    description: string;
    file_type: string;
    file_size: number;
    file_size_formatted: string;
    file_url: string;
    category: string;
    category_display: string;
    sync_status: string;
    sync_status_display: string;
    sync_error: string | null;
    synced_at: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Documentos del Chatbot', isCurrent: true }
];

const ChatDocuments = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const history = useHistory();
    const { 
        fetchingGetDocuments, 
        getDocuments, 
        activeDocument, 
        deleteDocument,
        syncDocument 
    } = useChatService();

    const { auth } = useContext(AuthContext);

    const [documentIdEdit, setDocumentIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [documents, setDocuments] = useState<ChatDocument[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showingCreate, setShowingCreate] = useState(false);

    const [filter, setFilter] = useState({
        page: 1,
        per_page: 10,
        sort: 'created_at',
        order: 'desc',
        search: '',
        category: '',
        sync_status: '',
        is_active: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order, filter.search, filter.category, filter.sync_status, filter.is_active]);

    const reloadTable = () => {
        const filters: any = {};
        if (filter.search) filters.search = filter.search;
        if (filter.category) filters.category = filter.category;
        if (filter.sync_status) filters.sync_status = filter.sync_status;
        if (filter.is_active !== '') filters.is_active = filter.is_active;
        
        getDocuments(filters, {
            onSuccess: (response: ServiceResponse) => {
                setDocuments(response.data.documents || []);
                setTotalRows(response.data.total || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (documentId: number) => {
        setShowingEdit(true);
        setDocumentIdEdit(documentId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setDocumentIdEdit(-1);
    };

    const onError = () => {
        setShowingEdit(false);
        setDocumentIdEdit(-1);
    };

    const active = (documentId: number) => {
        const doc = documents.find(d => d.id === documentId);
        const action = doc?.is_active ? 'Desactivando' : 'Activando';
        
        if (showLoading) showLoading('loading', `${action}...`);
        activeDocument(documentId, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    const sync = (documentId: number) => {
        if (showLoading) showLoading('loading', 'Sincronizando con OpenAI...');
        syncDocument(documentId, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    const destroy = (documentId: number) => {
        const doc = documents.find(d => d.id === documentId);
        const _text = `Está a punto de eliminar el documento: "${doc?.name}". También se eliminará de la base de conocimiento del chatbot.`;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando documento...');
                        deleteDocument(documentId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El documento no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <div className="container-fluid">
                <div className="page-container">
                    {/* Header con breadcrumbs */}
                    <PageHeader
                        title=""
                        breadcrumbs={breadcrumbs}
                    />

                    {/* Header con título y botón de crear alineados */}
                    <DefaultCard>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h1 className="page-title">Gestión de Documentos del Chatbot</h1>
                                <p className="page-description">
                                    Administra los documentos que usa el asistente de IA para responder preguntas.
                                    Los documentos se sincronizan automáticamente con OpenAI para búsqueda semántica.
                                </p>
                            </div>
                            <ButtonGeneral
                                onClick={showCreate}
                                title="Nuevo documento"
                                variant="primary"
                                size="small"
                                icon="fa-plus"
                                iconPosition="left"
                            />
                        </div>

                        {/* Data Table */}
                        <ChatDocumentDataTable
                            active={active}
                            sync={sync}
                            documents={documents}
                            totalRows={totalRows}
                            loading={fetchingGetDocuments}
                            edit={showEdit}
                            destroy={destroy}
                            filter={filter}
                            setFilter={setFilter}
                            callbackSuccess={reloadTable}
                            onSearch={reloadTable}
                        />
                    </DefaultCard>
                </div>
            </div>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Agregar documento al chatbot"
                    backdrop={true}
                    showFooter={false}
                >
                    <ChatDocumentCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar documento"
                    backdrop={true}
                    showFooter={false}
                >
                    <ChatDocumentEdit
                        documentId={documentIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={onError}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default ChatDocuments;

